import { useState, useEffect } from 'react';
import api from '../lib/api';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Card } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Download, Upload, FileSpreadsheet, CheckCircle, AlertCircle } from 'lucide-react';

const MODULES = ['companies', 'sites', 'users', 'suppliers', 'categories', 'products', 'machines'];

export default function ImportsPage() {
  const [module, setModule] = useState('companies');
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [executing, setExecuting] = useState(false);
  const [history, setHistory] = useState([]);
  const [tab, setTab] = useState('upload');

  useEffect(() => { api.get('/imports/history').then(r => setHistory(r.data)).catch(() => {}); }, []);

  const downloadTemplate = async () => {
    try {
      const res = await api.get(`/imports/templates/${module}`, { responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a'); a.href = url; a.download = `${module}_template.xlsx`; a.click();
      toast.success('Template downloaded');
    } catch { toast.error('Download failed'); }
  };

  const handleUpload = async () => {
    if (!file) { toast.error('Select a file'); return; }
    setUploading(true);
    const fd = new FormData(); fd.append('file', file); fd.append('module', module);
    try {
      const res = await api.post('/imports/upload', fd);
      setPreview(res.data);
      toast.success(`${res.data.valid} valid rows, ${res.data.errors?.length || 0} errors`);
    } catch (err) { toast.error(err.response?.data?.detail || 'Upload failed'); }
    finally { setUploading(false); }
  };

  const handleExecute = async () => {
    if (!preview?.batch_id) return;
    setExecuting(true);
    try {
      const res = await api.post(`/imports/execute/${preview.batch_id}`);
      toast.success(`Import complete: ${res.data.success} success, ${res.data.failed} failed`);
      setPreview(null); setFile(null);
      api.get('/imports/history').then(r => setHistory(r.data));
    } catch (err) { toast.error(err.response?.data?.detail || 'Import failed'); }
    finally { setExecuting(false); }
  };

  return (
    <div data-testid="imports-page">
      <div className="page-header">
        <div><h1 className="page-title">Excel Import</h1><p className="text-sm text-slate-500 mt-1">Bulk upload data using Excel templates</p></div>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="mb-6">
          <TabsTrigger value="upload" data-testid="tab-upload">Upload</TabsTrigger>
          <TabsTrigger value="history" data-testid="tab-history">History</TabsTrigger>
        </TabsList>

        <TabsContent value="upload">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Step 1: Select & Download */}
            <Card className="p-6 bg-white border border-slate-200">
              <h3 className="text-sm font-semibold text-slate-900 mb-4 font-heading flex items-center gap-2"><Download size={16} /> 1. Download Template</h3>
              <div className="space-y-3">
                <div className="form-field">
                  <Label>Module</Label>
                  <Select value={module} onValueChange={setModule}><SelectTrigger className="bg-slate-50" data-testid="import-module-select"><SelectValue /></SelectTrigger><SelectContent>{MODULES.map(m => <SelectItem key={m} value={m}>{m.charAt(0).toUpperCase() + m.slice(1)}</SelectItem>)}</SelectContent></Select>
                </div>
                <Button variant="outline" className="w-full" onClick={downloadTemplate} data-testid="download-template-btn"><Download size={14} className="mr-1.5" />Download Template</Button>
              </div>
            </Card>

            {/* Step 2: Upload */}
            <Card className="p-6 bg-white border border-slate-200">
              <h3 className="text-sm font-semibold text-slate-900 mb-4 font-heading flex items-center gap-2"><Upload size={16} /> 2. Upload File</h3>
              <div className="space-y-3">
                <div className="border-2 border-dashed border-slate-200 rounded-lg p-6 text-center hover:border-slate-400 transition-colors">
                  <FileSpreadsheet size={32} className="mx-auto text-slate-300 mb-2" />
                  <Input type="file" accept=".xlsx,.xls" onChange={e => setFile(e.target.files[0])} className="bg-slate-50" data-testid="import-file-input" />
                  {file && <p className="text-xs text-slate-500 mt-2">{file.name}</p>}
                </div>
                <Button className="w-full bg-slate-900 hover:bg-slate-800" onClick={handleUpload} disabled={!file || uploading} data-testid="upload-import-btn">{uploading ? 'Uploading...' : 'Upload & Preview'}</Button>
              </div>
            </Card>

            {/* Step 3: Execute */}
            <Card className="p-6 bg-white border border-slate-200">
              <h3 className="text-sm font-semibold text-slate-900 mb-4 font-heading flex items-center gap-2"><CheckCircle size={16} /> 3. Execute Import</h3>
              {preview ? (
                <div className="space-y-3">
                  <div className="bg-slate-50 rounded-lg p-3 text-sm space-y-1">
                    <p className="text-slate-600">Total rows: <span className="font-medium text-slate-900">{preview.total}</span></p>
                    <p className="text-emerald-600">Valid: <span className="font-medium">{preview.valid}</span></p>
                    {preview.errors?.length > 0 && <p className="text-red-600">Errors: <span className="font-medium">{preview.errors.length}</span></p>}
                  </div>
                  {preview.errors?.length > 0 && (
                    <div className="max-h-32 overflow-y-auto text-xs text-red-600 space-y-0.5">
                      {preview.errors.map((e, i) => <p key={i} className="flex items-start gap-1"><AlertCircle size={12} className="mt-0.5 flex-shrink-0" />{e}</p>)}
                    </div>
                  )}
                  <Button className="w-full bg-orange-600 hover:bg-orange-700 text-white" onClick={handleExecute} disabled={executing || preview.valid === 0} data-testid="execute-import-btn">{executing ? 'Importing...' : `Import ${preview.valid} Records`}</Button>
                </div>
              ) : (
                <p className="text-sm text-slate-400 text-center py-6">Upload a file to see preview</p>
              )}
            </Card>
          </div>

          {/* Preview Table */}
          {preview?.preview && preview.preview.length > 0 && (
            <Card className="mt-6 p-6 bg-white border border-slate-200">
              <h3 className="text-sm font-semibold text-slate-900 mb-3 font-heading">Preview (first 20 rows)</h3>
              <div className="data-table-wrapper max-h-64 overflow-auto">
                <Table>
                  <TableHeader><TableRow className="bg-slate-50">
                    <TableHead className="text-xs font-semibold uppercase tracking-wider text-slate-500">Row</TableHead>
                    <TableHead className="text-xs font-semibold uppercase tracking-wider text-slate-500">Status</TableHead>
                    <TableHead className="text-xs font-semibold uppercase tracking-wider text-slate-500">Data</TableHead>
                    <TableHead className="text-xs font-semibold uppercase tracking-wider text-slate-500">Errors</TableHead>
                  </TableRow></TableHeader>
                  <TableBody>
                    {preview.preview.map((r, i) => (
                      <TableRow key={i} className="hover:bg-slate-50/50">
                        <TableCell className="text-sm">{r.row_num}</TableCell>
                        <TableCell><span className={`status-badge status-${r.status === 'valid' ? 'active' : 'rejected'}`}>{r.status}</span></TableCell>
                        <TableCell className="text-xs text-slate-600 font-mono max-w-md truncate">{JSON.stringify(r.data)}</TableCell>
                        <TableCell className="text-xs text-red-600">{r.errors?.join(', ')}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </Card>
          )}
        </TabsContent>

        <TabsContent value="history">
          <div className="data-table-wrapper">
            <Table>
              <TableHeader><TableRow className="bg-slate-50 border-b border-slate-200">
                <TableHead className="text-xs font-semibold uppercase tracking-wider text-slate-500">Batch ID</TableHead>
                <TableHead className="text-xs font-semibold uppercase tracking-wider text-slate-500">Module</TableHead>
                <TableHead className="text-xs font-semibold uppercase tracking-wider text-slate-500">File</TableHead>
                <TableHead className="text-xs font-semibold uppercase tracking-wider text-slate-500">Total</TableHead>
                <TableHead className="text-xs font-semibold uppercase tracking-wider text-slate-500">Valid</TableHead>
                <TableHead className="text-xs font-semibold uppercase tracking-wider text-slate-500">Status</TableHead>
                <TableHead className="text-xs font-semibold uppercase tracking-wider text-slate-500">Date</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {history.length === 0 ? <TableRow><TableCell colSpan={7} className="text-center py-8 text-slate-400">No import history</TableCell></TableRow> :
                history.map(h => (
                  <TableRow key={h.batch_id} className="hover:bg-slate-50/50">
                    <TableCell className="font-mono text-xs">{h.batch_id}</TableCell>
                    <TableCell className="text-sm capitalize">{h.module}</TableCell>
                    <TableCell className="text-sm text-slate-600">{h.filename}</TableCell>
                    <TableCell className="text-sm">{h.total_rows}</TableCell>
                    <TableCell className="text-sm text-emerald-600">{h.valid_rows}</TableCell>
                    <TableCell><span className={`status-badge status-${h.status === 'completed' ? 'approved' : 'submitted'}`}>{h.status}</span></TableCell>
                    <TableCell className="text-xs text-slate-500">{new Date(h.created_at).toLocaleDateString()}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
